import * as hdf5 from 'jsfive';
import * as PIXI from 'pixi.js-legacy';
import { Dataset } from './types';

export enum GL_FORMATS {
  RED = 0x1903,
  RGBA = 0x1908,
  RGB = 0x1907,
  // NOTE: There are additional WebGL formats that could be used.
}

export enum GL_INTERNAL_FORMATS {
  R16F = 0x822d,
  R32F = 0x822e,
  // NOTE: There are additional WebGL formats that could be used.
}

export interface ImageBuffer {
  buffer: Uint8Array | Int16Array | Float32Array;
  width: number;
  height: number;
  format: GL_FORMATS;
  internalFormat: GL_INTERNAL_FORMATS;
  type: PIXI.TYPES;
}

/**
 * We could set the visibility to false but that must actually remove the mesh because it messes up the CPR centerline centering.
 */
export const blankImageBuffer = {
  width: 1,
  height: 1,
  buffer: new Float32Array([-10000]),
  format: GL_FORMATS.RED,
  internalFormat: GL_INTERNAL_FORMATS.R32F,
  type: PIXI.TYPES.FLOAT,
};

class CustomBufferResource extends PIXI.Resource {
  data: Uint8Array | Int16Array | Float32Array;
  format: GL_FORMATS;
  internalFormat: GL_INTERNAL_FORMATS;
  type: PIXI.TYPES;

  constructor(
    data: Uint8Array | Int16Array | Float32Array,
    width: number,
    height: number,
    format: GL_FORMATS,
    internalFormat: GL_INTERNAL_FORMATS,
    type: PIXI.TYPES
  ) {
    super(width, height);
    this.data = data;
    this.format = format;
    this.internalFormat = internalFormat;
    this.type = type;
  }

  upload(renderer: PIXI.Renderer, baseTexture: PIXI.BaseTexture, glTexture: any) {
    if (baseTexture.target == null) {
      return false;
    }

    const gl = renderer.gl;

    gl.pixelStorei(
      gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL,
      baseTexture.alphaMode === 1 // PIXI.ALPHA_MODES.UNPACK but `PIXI.ALPHA_MODES` are not exported
    );

    glTexture.width = baseTexture.width;
    glTexture.height = baseTexture.height;

    gl.texImage2D(
      // Note, a GL Target Type is a number.
      // See: https://pixijs.download/dev/docs/PIXI.html#TARGETS
      baseTexture.target.valueOf(),
      0,
      this.internalFormat,
      baseTexture.width,
      baseTexture.height,
      0,
      this.format,
      this.type,
      this.data
    );

    return true;
  }
}

/**
 * Create an image buffer object from a dataset object.
 * If transpose is true the image data rows and columns will be flipped as will the shape (ie [X, Y] vs the default [Y, X]).
 * @param useFloat32 If true the buffer will be created using 32 bit FLOAT data and loaded as a single red channel 32 bit FLOAT texture.
 *                   If false the buffer will be created using 16 bit SHORT data and loaded as a single red channel 16 bit FLOAT texture.
 *                   (NOTE: prior to loading the texture it will need to be converted to a temporary Float32Array as WebGL doesn't support loading from an Int16Array).
 */
export const createImageBufferFromDataset = (
  dataset: Dataset,
  transpose: boolean,
  useFloat32: boolean = false
): ImageBuffer => {
  const width = dataset.shape[transpose ? 0 : 1];
  const height = dataset.shape[transpose ? 1 : 0];
  const buffer: Int16Array | Float32Array = useFloat32
    ? new Float32Array(width * height)
    : new Int16Array(width * height);
  const data = dataset.value;
  // By default the data is stored row by row.
  if (!transpose) {
    for (let index = 0; index < width * height; index++) {
      buffer[index] = data[index];
    }
  }
  // If the image is transposed then rather than being stored row by row it is stored column by column.
  else {
    let index = 0;
    for (let j = 0; j < height; j++) {
      for (let i = 0; i < width; i++) {
        buffer[index++] = data[i * height + j];
      }
    }
  }

  return {
    buffer,
    width,
    height,
    internalFormat: useFloat32 ? GL_INTERNAL_FORMATS.R32F : GL_INTERNAL_FORMATS.R16F,
    format: GL_FORMATS.RED,
    type: PIXI.TYPES.FLOAT,
  };
};

// Utility function
const createHDF = (data: ArrayBuffer) => {
  return new hdf5.File(data, '');
};

/**
 * Take a raw buffer of imageData, convert it into a H5 file, then extract the image data out into an ImageBuffer.
 */
export const createImageBufferFromImageData = (imageData: ArrayBuffer, transpose: boolean): ImageBuffer => {
  if (imageData) {
    const file = createHDF(imageData);
    if (file) {
      return createImageBufferFromDataset(file.get('data'), transpose);
    }
  }
  // If something went wrong we return a default fallback buffer.
  return blankImageBuffer;
};

/**
 * Create a new PIXI.Texture from a ImageBuffer definition.
 */
export const createTexture = (imageBuffer: ImageBuffer): PIXI.Texture => {
  // If the buffer is an Int16Array and the internalFormat R16F we need to convert the buffer to a Float32Array before loading it (WebGL doesn't support loading from a Int16Array).
  const convertBufferToFloat32 =
    imageBuffer.buffer[Symbol.toStringTag] === 'Int16Array' && imageBuffer.internalFormat === GL_INTERNAL_FORMATS.R16F;
  const buffer = convertBufferToFloat32 ? Float32Array.from(imageBuffer.buffer) : imageBuffer.buffer;
  const resource = new CustomBufferResource(
    buffer,
    imageBuffer.width,
    imageBuffer.height,
    imageBuffer.format,
    imageBuffer.internalFormat,
    imageBuffer.type
  );
  return new PIXI.Texture(new PIXI.BaseTexture(resource, { scaleMode: PIXI.SCALE_MODES.LINEAR }));
};
